import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../layout/AdminLayout';
import { PageHeader } from '../common/PageHeader';
import { DataTable } from '../common/DataTable';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../ui/dialog';
import { instructorApi, settingsApi, authApi } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { UserDataTransferDialog } from '../common/UserDataTransferDialog';
import { toast } from 'sonner';
import { Loader2, Search, Mail, Phone, Users, ChevronRight, UserCog, Send, ArrowRightLeft } from 'lucide-react';

const emptyForm = {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postal_code: '',
    birth_date: '',
    qualifications: '',
    is_bgpr_herzgruppenleiter: false,
    notes: '',
};

export function InstructorsPage() {
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [instructors, setInstructors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [transferDialogOpen, setTransferDialogOpen] = useState(false);
    const [formData, setFormData] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    const [counts, setCounts] = useState({ active: 0, archived: 0 });
    const [qualificationOptions, setQualificationOptions] = useState([]);

    useEffect(() => {
        loadInstructors();
        loadCounts();
        settingsApi.getQualifications().then(res => setQualificationOptions(res.data.options || [])).catch(() => {});
    }, [showArchived]);

    const loadInstructors = async () => {
        setLoading(true);
        try {
            const response = await instructorApi.getAll(showArchived);
            setInstructors(response.data);
        } catch (error) {
            toast.error('Fehler beim Laden der Übungsleiter');
        } finally {
            setLoading(false);
        }
    };

    const loadCounts = async () => {
        try {
            const response = await instructorApi.getCounts();
            setCounts(response.data);
        } catch (error) {
            // ignore
        }
    };

    const handleOpenDialog = () => {
        setFormData(emptyForm);
        setDialogOpen(true);
    };

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            await instructorApi.create(formData);
            toast.success('Übungsleiter erstellt');
            setDialogOpen(false);
            loadInstructors();
            loadCounts();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
        } finally {
            setSaving(false);
        }
    };

    const handleImpersonate = async (userId, firstName, lastName) => {
        try {
            const res = await authApi.impersonate(userId);
            const { token, user: u } = res.data;
            const url = `/portal/impersonate?token=${encodeURIComponent(token)}&user=${encodeURIComponent(JSON.stringify(u))}`;
            window.open(url, '_blank', 'noopener');
            toast.success(`Angemeldet als ${firstName} ${lastName} (neuer Tab)`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Impersonation fehlgeschlagen');
        }
    };

    const filteredInstructors = instructors.filter((i) =>
        `${i.first_name} ${i.last_name} ${i.email}`.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const columns = [
        {
            header: 'Name',
            cell: (row) => (
                <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${row.is_archived ? 'bg-gray-200 text-gray-500' : 'bg-primary/10 text-primary'}`}>
                        {(row.first_name?.[0] || '').toUpperCase()}{(row.last_name?.[0] || '').toUpperCase()}
                    </div>
                    <div className="flex-1">
                        <p className={`font-medium flex items-center gap-2 ${row.is_archived ? 'text-muted-foreground line-through' : ''}`}>
                            {row.first_name} {row.last_name}
                            {row.role === 'admin' && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary uppercase tracking-wider" title="Administrator">
                                    Admin
                                </span>
                            )}
                        </p>
                        {row.qualifications && (
                            <p className="text-xs text-muted-foreground">{row.qualifications}</p>
                        )}
                    </div>
                </div>
            ),
        },
        {
            header: 'Kontakt',
            cell: (row) => (
                <div className="space-y-1">
                    <div className="flex items-center gap-2 text-sm">
                        <Mail className="h-3 w-3 text-muted-foreground shrink-0" />
                        {row.email}
                    </div>
                    {row.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <Phone className="h-3 w-3 shrink-0" />
                            {row.phone}
                        </div>
                    )}
                </div>
            ),
        },
        {
            header: 'Buchungen',
            cell: (row) => (
                <div className="flex items-center gap-2 text-xs">
                    <span className="inline-flex items-center px-2 py-0.5 rounded bg-muted/60 text-foreground font-medium">
                        {row.bookings_count || 0}
                    </span>
                    {row.attended_count > 0 && (
                        <span className="text-muted-foreground">({row.attended_count} besucht)</span>
                    )}
                </div>
            ),
        },
        {
            header: 'Lizenzen',
            cell: (row) => (
                <div className="flex items-center gap-2 text-xs">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full font-medium ${row.active_licenses > 0 ? 'bg-primary/10 text-primary' : 'bg-muted/40 text-muted-foreground'}`}>
                        {row.active_licenses || 0}
                    </span>
                    {row.pending_renewal && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-medium">
                            Verlängerung läuft
                        </span>
                    )}
                </div>
            ),
        },
        {
            header: 'UE',
            cell: (row) => (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${(row.available_ue || 0) >= 15 ? 'bg-green-100 text-green-800' : (row.available_ue || 0) > 0 ? 'bg-amber-100 text-amber-800' : 'bg-muted/30 text-muted-foreground'}`}>
                    {row.available_ue || 0} UE
                </span>
            ),
        },
        {
            header: '',
            cell: (row) => (
                <div className="flex items-center gap-1 justify-end">
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleImpersonate(row.id, row.first_name, row.last_name);
                        }}
                        className="p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
                        title="Als dieser Nutzer einloggen (neuer Tab)"
                        data-testid={`impersonate-btn-${row.id}`}
                    >
                        <UserCog className="h-4 w-4" />
                    </button>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/50" />
                </div>
            ),
        },
    ];

    return (
        <AdminLayout>
            <PageHeader
                title="Akademie-Mitglieder"
                description="Alle registrierten Teilnehmer und Übungsleiter der BGPR-Akademie"
                actions={
                    <div className="flex gap-2 flex-wrap">
                        {currentUser?.role === 'super_admin' && (
                            <Button
                                variant="outline"
                                onClick={() => setTransferDialogOpen(true)}
                                data-testid="open-data-transfer-btn"
                                title="Datensätze von einem Mitglied auf ein anderes übertragen (Super-Admin)"
                            >
                                <ArrowRightLeft className="h-4 w-4 mr-2" />
                                Daten zwischen Konten übertragen
                            </Button>
                        )}
                        <Button onClick={() => navigate('/admin/newsletter')} data-testid="open-newsletter-btn">
                            <Send className="h-4 w-4 mr-2" />
                            Newsletter / Informationsmail
                        </Button>
                    </div>
                }
            />

            {/* Search */}
            <div className="mb-6">
                <div className="relative max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Suchen nach Name oder E-Mail..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-9"
                        data-testid="search-members"
                    />
                </div>
            </div>

            {/* Table */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : (
                <DataTable
                    columns={columns}
                    data={filteredInstructors}
                    onRowClick={(row) => navigate(`/admin/instructors/${row.id}`)}
                    emptyMessage="Noch keine Akademie-Mitglieder registriert"
                />
            )}

            {/* Create Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading">Neuer Übungsleiter</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit}>
                        <div className="grid grid-cols-2 gap-4 py-4">
                            <div className="space-y-2">
                                <Label htmlFor="first_name">Vorname *</Label>
                                <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} required data-testid="instructor-firstname" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="last_name">Nachname *</Label>
                                <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} required data-testid="instructor-lastname" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="email">E-Mail *</Label>
                                <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="instructor-email" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="phone">Telefon</Label>
                                <Input id="phone" name="phone" value={formData.phone} onChange={handleChange} data-testid="instructor-phone" />
                            </div>
                            <div className="space-y-2 col-span-2">
                                <Label htmlFor="address">Adresse</Label>
                                <Input id="address" name="address" value={formData.address} onChange={handleChange} data-testid="instructor-address" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="postal_code">PLZ</Label>
                                <Input id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleChange} data-testid="instructor-postal" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="city">Stadt</Label>
                                <Input id="city" name="city" value={formData.city} onChange={handleChange} data-testid="instructor-city" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="birth_date">Geburtsdatum</Label>
                                <Input id="birth_date" name="birth_date" type="date" value={formData.birth_date} onChange={handleChange} data-testid="instructor-birthdate" />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="qualifications">Berufliche Vorqualifikation</Label>
                                <select
                                    id="qualifications"
                                    name="qualifications"
                                    value={formData.qualifications}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 rounded-md border bg-background text-sm"
                                    data-testid="instructor-qualifications"
                                >
                                    <option value="">-- Bitte wählen --</option>
                                    {qualificationOptions.map((q) => (
                                        <option key={q} value={q}>{q}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="space-y-2 col-span-2 pt-2 border-t">
                                <label className="flex items-center gap-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={!!formData.is_bgpr_herzgruppenleiter}
                                        onChange={(e) => setFormData({ ...formData, is_bgpr_herzgruppenleiter: e.target.checked })}
                                        className="w-5 h-5 rounded text-primary"
                                        data-testid="instructor-herzgruppenleiter"
                                    />
                                    <div>
                                        <span className="font-medium text-sm">BGPR-Herzgruppenleiter</span>
                                        <p className="text-xs text-muted-foreground">Zertifizierter Herzgruppenleiter der BGPR</p>
                                    </div>
                                </label>
                            </div>
                            <div className="space-y-2 col-span-2">
                                <Label htmlFor="notes">Notizen</Label>
                                <Textarea id="notes" name="notes" value={formData.notes} onChange={handleChange} rows={3} data-testid="instructor-notes" />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Abbrechen</Button>
                            <Button type="submit" disabled={saving} data-testid="save-instructor-button">
                                {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Erstellen
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Datensatz-Übertragung zwischen Mitgliedern (Super-Admin only) */}
            {currentUser?.role === 'super_admin' && (
                <UserDataTransferDialog
                    open={transferDialogOpen}
                    onOpenChange={setTransferDialogOpen}
                    onDone={() => { loadInstructors(); loadCounts(); }}
                />
            )}
        </AdminLayout>
    );
}
